import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "../../context/AuthContext";
import { getApiEndpoint } from "../../config/config";
import { attendanceService } from "../../services/attendanceService";
import { handleGenericError } from "../../utils/errorHandler";
import "./QuickCreateEventPopup.css";

const MaterialIcon = ({ name, className = "" }) => (
  <span className={`material-icons qcep-material-icon ${className}`.trim()}>
    {name}
  </span>
);

const MEETING_TYPE_OPTIONS = [
  { value: "team_meeting", label: "Team Meeting" },
  { value: "rep_meeting", label: "Rep Meeting" },
  { value: "recruiting_meeting", label: "Recruiting Meeting" },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Convert a Date to "YYYY-MM-DDThh:mm" for <input type="datetime-local"> */
const toLocalDTInput = d => {
  const dt = d instanceof Date ? d : new Date(d);
  const pad = n => String(n).padStart(2, "0");
  return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}T${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
};

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * A small Google-Calendar–style popup that appears when the user drags on the
 * calendar. It lets the manager quickly create a team event (and optionally a
 * QR attendance event at the same time).
 *
 * Props
 *   isOpen         – boolean
 *   slot           – { start: Date, end: Date } from react-big-calendar onSelectSlot
 *   onClose        – () => void
 *   onEventCreated – callback after save (refreshes the calendar)
 *   onMoreOptions  – ({ title, start, end, …prefill }) => void  opens full modal
 */
function QuickCreateEventPopup({
  isOpen,
  slot,
  event = null,
  onClose,
  onEventCreated,
  onMoreOptions,
}) {
  const { getVerifiedJWT } = useAuth();
  const isEditMode = Boolean(event?.id);

  const titleRef = useRef(null);

  // ── Form state ──────────────────────────────────────────────────────────────
  const [title, setTitle] = useState("");
  const [start, setStart] = useState(new Date());
  const [end, setEnd] = useState(new Date());
  const [meetingType, setMeetingType] = useState("team_meeting");
  const [repeatValue, setRepeatValue] = useState("none");
  const [selectedReps, setSelectedReps] = useState([]); // [{ id, label }]
  const [selectedGroups, setSelectedGroups] = useState([]); // [{ id, label }]
  const [createQrCode, setCreateQrCode] = useState(false);
  const [location, setLocation] = useState("");
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);

  // ── Assignee search ──────────────────────────────────────────────────────────
  const [reps, setReps] = useState([]);
  const [groups, setGroups] = useState([]);
  const [assignSearch, setAssignSearch] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);

  // ── Reset on open ────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!isOpen) return;

    if (event) {
      const s = event?.start ? new Date(event.start) : new Date();
      const e = event?.end
        ? new Date(event.end)
        : new Date(s.getTime() + 60 * 60_000);
      setStart(s);
      setEnd(e);
      setTitle(event.title || "");
      setLocation(event.location || "");
      setDescription(event.description || "");
      setMeetingType("team_meeting");
      setRepeatValue("none");
      setCreateQrCode(false);
      setSelectedReps([]);
      setSelectedGroups([]);
      setAssignSearch("");
      fetchReps();
      fetchGroups();
      setTimeout(() => titleRef.current?.focus(), 120);
      return;
    }

    const s = slot?.start ? new Date(slot.start) : new Date();
    const e = slot?.end
      ? new Date(slot.end)
      : new Date(s.getTime() + 60 * 60_000);

    // Ensure end is at least 15 min after start
    if (e <= s) e.setTime(s.getTime() + 60 * 60_000);

    setStart(s);
    setEnd(e);
    setTitle("");
    setLocation("");
    setDescription("");
    setMeetingType("team_meeting");
    setRepeatValue("none");
    setCreateQrCode(false);
    setSelectedReps([]);
    setSelectedGroups([]);
    setAssignSearch("");

    fetchReps();
    fetchGroups();

    setTimeout(() => titleRef.current?.focus(), 120);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, slot, event]);

  const fetchReps = async () => {
    try {
      const jwt = await getVerifiedJWT();
      const res = await fetch(
        getApiEndpoint("/v1/company-downline/reps-by-manager"),
        {
          headers: { Authorization: `Bearer ${jwt}` },
        }
      );
      if (res.ok) {
        const data = await res.json();
        setReps(
          (data.data?.reps || []).map(r => ({
            id: r.rep_id.toString(),
            label: `${r.first_name} ${r.last_name}`,
          }))
        );
      }
    } catch {
      /* silent */
    }
  };

  const fetchGroups = async () => {
    try {
      const jwt = await getVerifiedJWT();
      const res = await fetch(getApiEndpoint("/v1/groups/owned"), {
        headers: { Authorization: `Bearer ${jwt}` },
      });
      if (res.ok) {
        const data = await res.json();
        setGroups(
          (data.data || []).map(g => ({
            id: g.id.toString(),
            label: g.name,
          }))
        );
      }
    } catch {
      /* silent */
    }
  };

  // ── Save ─────────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!title.trim()) {
      titleRef.current?.focus();
      return;
    }

    try {
      setSaving(true);
      const jwt = await getVerifiedJWT();

      const durationMinutes = Math.max(
        15,
        Math.round((end.getTime() - start.getTime()) / 60_000)
      );

      const payload = {
        event_title: title.trim(),
        event_description: description,
        scheduled_time: start.toISOString(),
        duration_minutes: durationMinutes,
        location,
      };

      const res = await fetch(
        getApiEndpoint(
          isEditMode
            ? `/v1/attendance/team-events/${event.id}`
            : "/v1/attendance/team-events"
        ),
        {
          method: isEditMode ? "PATCH" : "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
          body: JSON.stringify(
            isEditMode
              ? payload
              : {
                  ...payload,
                  assigned_rep_ids: selectedReps.map(r => parseInt(r.id)),
                  assigned_group_ids: selectedGroups.map(g => parseInt(g.id)),
                }
          ),
        }
      );

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(
          err?.message ||
            (isEditMode ? "Failed to update event" : "Failed to create event")
        );
      }

      const result = await res.json().catch(() => ({}));

      if (!isEditMode && createQrCode) {
        try {
          await attendanceService.createAttendanceEvent(
            {
              title: title.trim(),
              description,
              scheduled_time: start.toISOString(),
              duration_minutes: durationMinutes,
              location,
            },
            jwt
          );
        } catch {
          /* Don't block the whole save if QR creation fails */
        }
      }

      if (onEventCreated) {
        onEventCreated({
          result,
          optimisticEvent: isEditMode
            ? null
            : {
                id: `pending-${Date.now()}`,
                title: title.trim(),
                start: new Date(start),
                end: new Date(end),
                description,
                location,
                isPendingCreate: true,
              },
        });
      }
      onClose();
    } catch (err) {
      handleGenericError(
        err,
        isEditMode ? "Failed to update event" : "Failed to create event"
      );
    } finally {
      setSaving(false);
    }
  };

  // ── More options → hand off pre-filled data to full modal ────────────────────
  const handleMoreOptions = () => {
    if (onMoreOptions) {
      onMoreOptions({
        event,
        title,
        start,
        end,
        meetingType,
        location,
        description,
        selectedReps,
        selectedGroups,
      });
    }
    onClose();
  };

  // ── Assignee dropdown filtering ──────────────────────────────────────────────
  const selectedRepIds = new Set(selectedReps.map(r => r.id));
  const selectedGroupIds = new Set(selectedGroups.map(g => g.id));
  const lowerSearch = assignSearch.toLowerCase();

  const filteredGroups = assignSearch
    ? groups.filter(
        g =>
          !selectedGroupIds.has(g.id) &&
          g.label.toLowerCase().includes(lowerSearch)
      )
    : groups.filter(g => !selectedGroupIds.has(g.id)).slice(0, 5);

  const filteredReps = assignSearch
    ? reps.filter(
        r =>
          !selectedRepIds.has(r.id) &&
          r.label.toLowerCase().includes(lowerSearch)
      )
    : reps.filter(r => !selectedRepIds.has(r.id)).slice(0, 6);

  if (!isOpen) return null;

  const applyMeetingType = nextType => {
    setMeetingType(nextType);

    const selectedType = MEETING_TYPE_OPTIONS.find(
      option => option.value === nextType
    );
    const currentType = MEETING_TYPE_OPTIONS.find(
      option => option.value === meetingType
    );

    if (!title.trim() || (currentType && title.trim() === currentType.label)) {
      setTitle(selectedType?.label || "");
    }
  };

  return (
    <div className="qcep-overlay" onClick={onClose}>
      <div className="qcep-card" onClick={e => e.stopPropagation()}>
        {/* ── Header ── */}
        <div className="qcep-header">
          <span className="qcep-drag-dots">≡</span>
          <button className="qcep-close" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>

        {/* ── Title ── */}
        <input
          ref={titleRef}
          className="qcep-title-input"
          placeholder="Add title"
          value={title}
          onChange={e => setTitle(e.target.value)}
          onKeyDown={e => e.key === "Enter" && handleSave()}
        />

        <div
          className="qcep-type-toggle"
          role="tablist"
          aria-label="Meeting type"
        >
          {MEETING_TYPE_OPTIONS.map(option => (
            <button
              key={option.value}
              type="button"
              className={`qcep-type-option ${meetingType === option.value ? "active" : ""}`}
              onClick={() => applyMeetingType(option.value)}
            >
              {option.label}
            </button>
          ))}
        </div>

        <div className="qcep-divider" />

        {/* ── Time range ── */}
        <div className="qcep-row">
          <span className="qcep-icon">
            <MaterialIcon name="schedule" />
          </span>
          <div className="qcep-time-fields">
            <input
              type="datetime-local"
              className="qcep-datetime-input"
              value={toLocalDTInput(start)}
              onChange={e => {
                const d = new Date(e.target.value);
                if (!isNaN(d)) setStart(d);
              }}
            />
            <span className="qcep-time-dash">–</span>
            <input
              type="datetime-local"
              className="qcep-datetime-input"
              value={toLocalDTInput(end)}
              onChange={e => {
                const d = new Date(e.target.value);
                if (!isNaN(d)) setEnd(d);
              }}
            />
          </div>
        </div>

        {/* ── Repeats ── */}
        <div className="qcep-row">
          <span className="qcep-icon">
            <MaterialIcon name="repeat" />
          </span>
          <select
            className="qcep-select"
            value={repeatValue}
            onChange={e => setRepeatValue(e.target.value)}
          >
            <option value="none">Does not repeat</option>
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
          </select>
        </div>

        {/* ── Add reps / groups ── */}
        <div className="qcep-row qcep-assignee-row">
          <span className="qcep-icon">
            <MaterialIcon name="group" />
          </span>
          <div className="qcep-assignee-field">
            <div className="qcep-assignee-chips">
              {selectedGroups.map(g => (
                <span key={`g-${g.id}`} className="qcep-chip qcep-chip-group">
                  {g.label}
                  <button
                    onClick={() =>
                      setSelectedGroups(prev => prev.filter(x => x.id !== g.id))
                    }
                  >
                    ×
                  </button>
                </span>
              ))}
              {selectedReps.map(r => (
                <span key={r.id} className="qcep-chip">
                  {r.label}
                  <button
                    onClick={() =>
                      setSelectedReps(prev => prev.filter(x => x.id !== r.id))
                    }
                  >
                    ×
                  </button>
                </span>
              ))}
              <input
                className="qcep-assignee-input"
                placeholder={
                  selectedReps.length + selectedGroups.length === 0
                    ? "Add reps or groups"
                    : ""
                }
                value={assignSearch}
                onChange={e => {
                  setAssignSearch(e.target.value);
                  setShowDropdown(true);
                }}
                onFocus={() => setShowDropdown(true)}
                onBlur={() => setTimeout(() => setShowDropdown(false), 160)}
              />
            </div>

            {showDropdown &&
              (filteredGroups.length > 0 || filteredReps.length > 0) && (
                <div className="qcep-dropdown">
                  {filteredGroups.length > 0 && (
                    <>
                      <div className="qcep-dropdown-section">Groups</div>
                      {filteredGroups.map(g => (
                        <div
                          key={`g-${g.id}`}
                          className="qcep-dropdown-item"
                          onMouseDown={() => {
                            setSelectedGroups(prev => [...prev, g]);
                            setAssignSearch("");
                          }}
                        >
                          <span className="qcep-dropdown-icon">
                            <MaterialIcon name="group" />
                          </span>
                          {g.label}
                        </div>
                      ))}
                    </>
                  )}
                  {filteredReps.length > 0 && (
                    <>
                      <div className="qcep-dropdown-section">Reps</div>
                      {filteredReps.map(r => (
                        <div
                          key={r.id}
                          className="qcep-dropdown-item"
                          onMouseDown={() => {
                            setSelectedReps(prev => [...prev, r]);
                            setAssignSearch("");
                          }}
                        >
                          <span className="qcep-dropdown-icon">
                            <MaterialIcon name="person" />
                          </span>
                          {r.label}
                        </div>
                      ))}
                    </>
                  )}
                </div>
              )}
          </div>
        </div>

        {!isEditMode && (
          <div className="qcep-row">
            <span className="qcep-icon">
              <MaterialIcon name="qr_code_2" />
            </span>
            <label className="qcep-checkbox-label">
              <input
                type="checkbox"
                className="qcep-checkbox"
                checked={createQrCode}
                onChange={e => setCreateQrCode(e.target.checked)}
              />
              Create QR code attendance event
            </label>
          </div>
        )}

        {/* ── Location ── */}
        <div className="qcep-row">
          <span className="qcep-icon">
            <MaterialIcon name="location_on" />
          </span>
          <input
            className="qcep-input"
            placeholder="Add location"
            value={location}
            onChange={e => setLocation(e.target.value)}
          />
        </div>

        {/* ── Description ── */}
        <div className="qcep-row">
          <span className="qcep-icon">
            <MaterialIcon name="notes" />
          </span>
          <input
            className="qcep-input"
            placeholder="Add description"
            value={description}
            onChange={e => setDescription(e.target.value)}
          />
        </div>

        {/* ── Footer ── */}
        <div className="qcep-footer">
          <button className="qcep-more-options" onClick={handleMoreOptions}>
            More options
          </button>
          <button className="qcep-save" onClick={handleSave} disabled={saving}>
            {saving ? "Saving…" : isEditMode ? "Save" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default QuickCreateEventPopup;
